// A rhythm tree: the main rhythm (the root) and named branches, each a full score.
// Saved trees are a flat list of branches in parent-first order; each branch names its
// parent, which is ROOT or an earlier branch. The root's score is the rhythm's own score.
export const ROOT = "root";
export const MAX_BRANCHES = 40;
export const MAX_DEPTH = 8;
// Across every branch, so a saved tree stays a reasonable size.
export const MAX_TREE_MEASURES = 400;
export const ROOT_NAME = "Main rhythm";

export const childrenOf = (nodes, parentId) => nodes.filter((node) => node.parentId === parentId);
export const findNode = (nodes, id) => nodes.find((node) => node.id === id);

// Branches from the root's first child down to `id` (empty for the root).
export function pathTo(nodes, id) {
  const path = [];
  for (let node = findNode(nodes, id); node && node.id !== ROOT; node = findNode(nodes, node.parentId)) {
    path.unshift(node);
    if (path.length > nodes.length) break;
  }
  return path;
}
// `id` and everything beneath it.
export function subtreeIds(nodes, id) {
  const ids = new Set([id]);
  for (const node of nodes) if (ids.has(node.parentId)) ids.add(node.id);
  return ids;
}
export const descendantCount = (nodes, id) => subtreeIds(nodes, id).size - 1;

// "Child A", "Child B"… skipping names already used beside it.
export function nextChildName(nodes, parentId) {
  const taken = new Set(childrenOf(nodes, parentId).map((node) => node.name));
  for (let i = 0; ; i++) {
    const letters = i < 26 ? String.fromCharCode(65 + i) : `${String.fromCharCode(65 + (i % 26))}${Math.floor(i / 26)}`;
    if (!taken.has(`Child ${letters}`)) return `Child ${letters}`;
  }
}
export function uniqueName(nodes, parentId, name) {
  const taken = new Set(childrenOf(nodes, parentId).map((node) => node.name));
  if (!taken.has(name)) return name;
  for (let i = 2; ; i++) if (!taken.has(`${name} ${i}`)) return `${name} ${i}`;
}
export const newBranchId = () => Math.random().toString(36).slice(2, 10);

// A branch and its descendants as a tree of their own, with the branch as the new root.
export function subtreeAsTree(nodes, id) {
  const ids = subtreeIds(nodes, id);
  const branches = nodes.filter((node) => ids.has(node.id) && node.id !== id && node.id !== ROOT).map((node) => node.parentId === id ? { ...node, parentId: ROOT } : node);
  return branches.length ? { nodes: branches } : null;
}

// In Score, the tree keeps every branch except the one being edited, which is the live score.
// Returns every branch including the root, all up to date.
export function liveNodes({ score, tree }) {
  const currentId = tree?.currentId || ROOT;
  const live = (id, stored) => id === currentId ? score : stored;
  return [{ id: ROOT, parentId: null, name: ROOT_NAME, score: live(ROOT, tree?.rootScore) },
    ...(tree?.nodes || []).map((node) => ({ ...node, score: live(node.id, node.score) }))];
}
// What Score saves: the main rhythm's score, plus its branches if there are any.
export function treeForSaving(present) {
  const [root, ...branches] = liveNodes(present);
  return { score: root.score, tree: branches.length ? { nodes: branches.map(({ id, parentId, name, score }) => ({ id, parentId, name, score })) } : null };
}
// A saved rhythm's branches, including the root.
export const withRoot = (rhythm) => [{ id: ROOT, parentId: null, name: ROOT_NAME, score: rhythm.score }, ...(rhythm.tree?.nodes || [])];
